import { useEffect, useRef, useState } from 'react';
import { Icone } from '../../design/Icone';
import { abrirCameraTraseira, focarDeNovo, temCamera } from '../../leitor-de-codigo';
import { medidaReduzida, type FonteDaFoto } from './foto';
import './camera-da-foto.css';

/**
 * A CÂMERA DO INOVAPAY para as fotos de comprovação (dono, 02/10: "coloca pra usar a do InovaPay").
 *
 * Antes o botão chamava o app de câmera do celular (`<input capture>`). Ao apertar OK, o app
 * grava a foto no armazenamento do aparelho para entregar ao navegador — celular cheio dava
 * "memória cheia" e a foto morria ali; e a foto de 12–108 MP ainda era aberta inteira no Chrome.
 * Aqui a câmera fica dentro da página: o quadro é capturado já reduzido (≤ 1600px), nada é
 * gravado no celular e o Chrome nunca vai para segundo plano.
 *
 * Sem câmera no navegador (sem HTTPS, permissão negada, câmera em uso), a tela diz o porquê e
 * oferece a câmera do celular — o jeito antigo — em vez de travar a pessoa.
 */
export function CameraDaFoto({
  titulo,
  aoUsar,
  aoFechar,
}: {
  titulo: string;
  aoUsar: (foto: FonteDaFoto) => void;
  aoFechar: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const fluxo = useRef<MediaStream | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const [estado, setEstado] = useState<'ligando' | 'ok' | 'falha'>(
    temCamera() ? 'ligando' : 'falha',
  );
  const [falha, setFalha] = useState(
    temCamera() ? '' : 'Este navegador não abre a câmera dentro do InovaPay.',
  );
  const [capturada, setCapturada] = useState<{ quadro: HTMLCanvasElement; previa: string } | null>(
    null,
  );

  useEffect(() => {
    if (!temCamera()) return;
    let vivo = true;
    void abrirCameraTraseira()
      .then(async (s) => {
        // A tela fechou antes da câmera ligar: desliga já, senão ela fica presa ("em uso").
        if (!vivo || !video.current) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        fluxo.current = s;
        video.current.srcObject = s;
        await video.current.play();
        if (vivo) setEstado('ok');
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setEstado('falha');
        setFalha(
          e instanceof DOMException && e.name === 'NotAllowedError'
            ? 'A câmera está bloqueada para o InovaPay neste celular.'
            : 'A câmera não abriu (pode estar em uso por outro aplicativo).',
        );
      });
    return () => {
      vivo = false;
      fluxo.current?.getTracks().forEach((t) => t.stop());
      fluxo.current = null;
    };
  }, []);

  // A prévia é um arquivo na memória: solta quando troca ou quando a tela fecha.
  useEffect(
    () => () => {
      if (capturada) {
        URL.revokeObjectURL(capturada.previa);
        capturada.quadro.width = 0;
      }
    },
    [capturada],
  );

  const bater = () => {
    const v = video.current;
    if (!v?.videoWidth || !v.videoHeight) return;
    const { w, h } = medidaReduzida(v.videoWidth, v.videoHeight);
    const quadro = document.createElement('canvas');
    quadro.width = w;
    quadro.height = h;
    quadro.getContext('2d')?.drawImage(v, 0, 0, w, h);
    quadro.toBlob(
      (b) => {
        if (b) setCapturada({ quadro, previa: URL.createObjectURL(b) });
      },
      'image/jpeg',
      0.8,
    );
  };

  const usar = () => {
    if (!capturada) return;
    // Entrega o quadro (não o arquivo da prévia): quem chama carimba e envia.
    const copia = document.createElement('canvas');
    copia.width = capturada.quadro.width;
    copia.height = capturada.quadro.height;
    copia.getContext('2d')?.drawImage(capturada.quadro, 0, 0);
    aoUsar(copia);
  };

  return (
    <div className="cam" role="dialog" aria-label={titulo}>
      <div className="cam-topo">
        <span>{titulo}</span>
        <button type="button" onClick={aoFechar} aria-label="Fechar câmera">
          <Icone nome="x" tamanho={18} />
        </button>
      </div>

      {estado === 'falha' ? (
        <div className="cam-falha">
          <Icone nome="warn" tamanho={20} />
          <p>{falha}</p>
          <p className="cam-falha-dica">
            Dá para usar a câmera do celular. Se aparecer “memória cheia”, libere espaço no celular.
          </p>
          <button type="button" onClick={() => campo.current?.click()}>
            Usar a câmera do celular
          </button>
          <input
            ref={campo}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const arq = e.target.files?.[0];
              e.target.value = '';
              if (arq) aoUsar(arq);
            }}
          />
        </div>
      ) : (
        <>
          <div className="cam-video">
            <video
              ref={video}
              muted
              playsInline
              aria-label="Câmera — toque para focar"
              hidden={capturada !== null}
              onClick={() => {
                if (fluxo.current) void focarDeNovo(fluxo.current);
              }}
            />
            {capturada && <img src={capturada.previa} alt="Foto tirada" />}
            {estado === 'ligando' && <p className="cam-ligando">Ligando a câmera…</p>}
          </div>
          <div className="cam-rodape">
            {capturada ? (
              <>
                <button type="button" className="cam-secundario" onClick={() => setCapturada(null)}>
                  Tirar outra
                </button>
                <button type="button" className="cam-primario" onClick={usar}>
                  <Icone nome="check" tamanho={18} traco={2} /> Usar foto
                </button>
              </>
            ) : (
              <button
                type="button"
                className="cam-disparo"
                onClick={bater}
                disabled={estado !== 'ok'}
                aria-label="Tirar foto"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
